// OverviewMetricsTable.tsx — the commit-overview pane's metrics table: one
// row per metric, one column per selected commit.
//
// Row order is the experiment's objectives first, in their declared
// position order, then every other metric alphabetically — so the numbers
// the experiment exists to move are the ones at the top of the pane.
//
// The better value in each row is marked per the metric's own direction
// (lower_is_better vs higher_is_better), with a ★ and a weight change rather
// than color alone. A value that was fanned out to this commit rather than
// measured on it says so, because comparing an inherited number against a
// measured one is the mistake this table is meant to prevent. A dirty value
// — one an experiment's dirty mark says is not comparable — is struck
// through with the mark's reason on hover and never wins the ★.
import { StatusBadge } from 'common';
import type { CommitOverview, ValueOnCommit } from '../api';
import { cleanBest, dirtyTitle } from './dirty';
import DirtyValue from './DirtyValue';
import { formatNum, shortSha, subjectOf } from './format';

interface Props {
  commits: CommitOverview[];
  /** Objective metric names, position-ordered. */
  objectives: string[];
}

interface MetricRow {
  metric: string;
  unit: string;
  direction: ValueOnCommit['direction'];
  /** Value per commit sha; absent when the commit has no value for it. */
  byCommit: Map<string, ValueOnCommit>;
  objective: boolean;
}

export function buildRows(commits: CommitOverview[], objectives: string[]): MetricRow[] {
  const rows = new Map<string, MetricRow>();
  for (const c of commits) {
    for (const v of c.values ?? []) {
      let row = rows.get(v.metric);
      if (!row) {
        row = {
          metric: v.metric,
          unit: v.unit,
          direction: v.direction,
          byCommit: new Map(),
          objective: objectives.includes(v.metric),
        };
        rows.set(v.metric, row);
      }
      row.byCommit.set(c.commit.sha, v);
    }
  }
  // An objective with no value anywhere still earns a row: "declared and
  // never measured" is information, not an absence.
  objectives.forEach((n) => {
    if (!rows.has(n)) {
      rows.set(n, {
        metric: n,
        unit: '',
        direction: 'higher_is_better',
        byCommit: new Map(),
        objective: true,
      });
    }
  });
  const order = (r: MetricRow) => {
    const at = objectives.indexOf(r.metric);
    return at >= 0 ? at : objectives.length;
  };
  return Array.from(rows.values()).sort(
    (a, b) => order(a) - order(b) || a.metric.localeCompare(b.metric),
  );
}

// The best clean value in a row, or undefined when fewer than two commits
// carry a clean one (nothing to compare against). Dirty values never win.
export function bestValue(row: MetricRow): number | undefined {
  const vals = Array.from(row.byCommit.values());
  if (vals.filter((v) => !v.dirty).length < 2) return undefined;
  return cleanBest(vals, row.direction !== 'lower_is_better');
}

export default function OverviewMetricsTable({ commits, objectives }: Props) {
  const rows = buildRows(commits, objectives);
  if (rows.length === 0) {
    return (
      <div className="text-secondary">
        No metric values on the selected commit{commits.length === 1 ? '' : 's'}.
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Metric</th>
            {commits.map((c) => (
              <th
                key={c.commit.sha}
                title={[
                  subjectOf(c.commit.message),
                  c.commit.author_name || '',
                  c.commit.authored_at ? new Date(c.commit.authored_at).toLocaleString() : '',
                  (c.commit.branches ?? []).join(', '),
                ].filter(Boolean).join('\n')}
              >
                <code>{shortSha(c.commit.sha)}</code>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const best = bestValue(row);
            return (
              <tr key={row.metric}>
                <td>
                  <code>{row.metric}</code>
                  {row.unit && <span className="text-secondary"> ({row.unit})</span>}
                  <span className="text-secondary">
                    {' '}
                    {row.direction === 'higher_is_better' ? '↑' : '↓'}
                  </span>
                  {row.objective && (
                    <StatusBadge tone="primary" className="ms-2" uppercase={false}>
                      objective
                    </StatusBadge>
                  )}
                </td>
                {commits.map((c) => {
                  const v = row.byCommit.get(c.commit.sha);
                  if (!v) {
                    return (
                      <td key={c.commit.sha} className="text-secondary">
                        —
                      </td>
                    );
                  }
                  const isBest = best !== undefined && v.value === best && !v.dirty;
                  return (
                    <td
                      key={c.commit.sha}
                      title={[
                        dirtyTitle(v.dirty_marks),
                        v.target ? `target ${v.target}` : '',
                        v.dep_digest ? `dep_digest ${v.dep_digest}` : '',
                        v.inherited_from ? `measured at ${shortSha(v.inherited_from)}` : '',
                        v.submitted_by ? `by ${v.submitted_by}` : '',
                      ].filter(Boolean).join('\n')}
                    >
                      <DirtyValue
                        text={formatNum(v.value)}
                        dirty={v.dirty}
                        marks={v.dirty_marks}
                        className={isBest ? 'fw-bold' : undefined}
                      />
                      {isBest && <span title="Best of the selected commits"> ★</span>}
                      {v.dirty && (
                        <StatusBadge tone="neutral" className="ms-2" uppercase={false}>
                          dirty
                        </StatusBadge>
                      )}
                      {v.inherited_from && (
                        <StatusBadge tone="warning" className="ms-2" uppercase={false}>
                          inherited
                        </StatusBadge>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
